import React, { useState } from 'react';

export default function APIDocumentation() {
    const [activeSection, setActiveSection] = useState('overview');
    const [activeEndpoint, setActiveEndpoint] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [copiedCode, setCopiedCode] = useState(null);
    const [expandedSections, setExpandedSections] = useState(new Set(['getting-started']));

    // Table of contents structure
    const sections = [
        {
            id: 'overview',
            title: 'Overview',
            icon: '📖'
        },
        {
            id: 'getting-started',
            title: 'Getting Started',
            icon: '🚀',
            subsections: [
                { id: 'authentication', title: 'Authentication' },
                { id: 'api-keys', title: 'API Keys' },
                { id: 'rate-limiting', title: 'Rate Limiting' }
            ]
        },
        {
            id: 'endpoints',
            title: 'API Endpoints',
            icon: '🔌',
            subsections: [
                { id: 'schema', title: 'Schema Endpoints' },
                { id: 'relationships', title: 'Relationships Endpoints' },
                { id: 'features', title: 'Features Endpoints' },
                { id: 'events', title: 'Events Endpoints' }
            ]
        },
        {
            id: 'webhooks',
            title: 'Webhooks',
            icon: '🪝'
        },
        {
            id: 'examples',
            title: 'Code Examples',
            icon: '💻',
            subsections: [
                { id: 'python', title: 'Python' },
                { id: 'javascript', title: 'JavaScript' },
                { id: 'php', title: 'PHP' },
                { id: 'curl', title: 'cURL' }
            ]
        },
        {
            id: 'integration',
            title: 'Integration Guide',
            icon: '🔧'
        }
    ];

    // API endpoints data
    const endpoints = {
        schema: [
            {
                method: 'GET',
                path: '/api/internal/schema',
                description: 'Get complete database schema information',
                auth: 'Required',
                response: {
                    total_tables: 156,
                    tables: [
                        {
                            name: "users",
                            columns: 12,
                            has_primary_key: true,
                            has_foreign_keys: true,
                            indexes: 5,
                            description: "User accounts and authentication"
                        }
                    ]
                }
            },
            {
                method: 'GET',
                path: '/api/internal/schema/{table_name}',
                description: 'Get detailed schema for a specific table',
                auth: 'Required',
                params: [
                    { name: 'table_name', type: 'string', required: true, description: 'Name of the table' }
                ]
            }
        ],
        relationships: [
            {
                method: 'GET',
                path: '/api/internal/relationships',
                description: 'Get all foreign key relationships',
                auth: 'Required',
                queryParams: [
                    { name: 'table', type: 'string', required: false, description: 'Filter by table name' }
                ]
            }
        ],
        features: [
            {
                method: 'GET',
                path: '/api/internal/features',
                description: 'Get all platform features and their database dependencies',
                auth: 'Required'
            },
            {
                method: 'GET',
                path: '/api/internal/features/{feature_id}/impact',
                description: 'Analyze impact of changes to a feature',
                auth: 'Required'
            }
        ],
        events: [
            {
                method: 'GET',
                path: '/api/internal/events',
                description: 'Get event registry with all possible events',
                auth: 'Required'
            },
            {
                method: 'POST',
                path: '/api/internal/events/subscribe',
                description: 'Subscribe to webhook events',
                auth: 'Required',
                body: {
                    webhook_url: "https://your-app.com/webhooks",
                    events: ["user.created", "appointment.scheduled"],
                    secret: "your-webhook-secret"
                }
            }
        ]
    };

    const codeExamples = {
        python: `import requests
import json

# Configuration
API_KEY = "your-api-key-here"
BASE_URL = "https://internal.sys.dentulu.com"

headers = {
    "Authorization": f"Bearer {API_KEY}",
    "Content-Type": "application/json"
}

# Get schema information
response = requests.get(f"{BASE_URL}/api/internal/schema", headers=headers)
schema = response.json()

print(f"Total tables: {schema['total_tables']}")
for table in schema['tables'][:5]:
    print(f"- {table['name']}: {table['columns']} columns")`,
        
        javascript: `const axios = require('axios');

// Configuration
const API_KEY = 'your-api-key-here';
const BASE_URL = 'https://internal.sys.dentulu.com';

const client = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Authorization': \`Bearer \${API_KEY}\`,
        'Content-Type': 'application/json'
    }
});

// Get relationships
async function getRelationships() {
    try {
        const response = await client.get('/api/internal/relationships');
        console.log(\`Found \${response.data.total} relationships\`);
        return response.data;
    } catch (error) {
        console.error('Error:', error.response?.data || error.message);
    }
}`,
        
        php: `<?php
$apiKey = 'your-api-key-here';
$baseUrl = 'https://internal.sys.dentulu.com';

$headers = [
    'Authorization: Bearer ' . $apiKey,
    'Content-Type: application/json'
];

// Get features
$ch = curl_init($baseUrl . '/api/internal/features');
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$features = json_decode($response, true);

echo "Platform features:\\n";
foreach ($features['features'] as $feature) {
    echo "- {$feature['name']}: {$feature['description']}\\n";
}`,
        
        curl: `# Get schema
curl -X GET https://internal.sys.dentulu.com/api/internal/schema \\
  -H "Authorization: Bearer your-api-key-here" \\
  -H "Content-Type: application/json"

# Subscribe to webhooks
curl -X POST https://internal.sys.dentulu.com/api/internal/events/subscribe \\
  -H "Authorization: Bearer your-api-key-here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "webhook_url": "https://your-app.com/webhooks",
    "events": ["user.created", "appointment.scheduled"],
    "secret": "your-webhook-secret"
  }'`
    };

    const toggleSection = (sectionId) => {
        const newExpanded = new Set(expandedSections);
        if (newExpanded.has(sectionId)) {
            newExpanded.delete(sectionId);
        } else {
            newExpanded.add(sectionId);
        }
        setExpandedSections(newExpanded);
    };

    const copyCode = (code, id) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(id);
        setTimeout(() => setCopiedCode(null), 2000);
    };

    const filteredEndpoints = searchTerm
        ? Object.entries(endpoints).reduce((acc, [category, items]) => {
            const filtered = items.filter(endpoint =>
                endpoint.path.toLowerCase().includes(searchTerm.toLowerCase()) ||
                endpoint.description.toLowerCase().includes(searchTerm.toLowerCase())
            );
            if (filtered.length > 0) {
                acc[category] = filtered;
            }
            return acc;
        }, {})
        : endpoints;

    const renderContent = () => {
        switch (activeSection) {
            case 'overview':
                return <OverviewSection />;
            case 'authentication':
                return <AuthenticationSection copyCode={copyCode} copiedCode={copiedCode} />;
            case 'api-keys':
                return <ApiKeysSection />;
            case 'rate-limiting':
                return <RateLimitingSection />;
            case 'schema':
            case 'relationships':
            case 'features':
            case 'events':
                return <EndpointsSection 
                    category={activeSection} 
                    endpoints={filteredEndpoints[activeSection] || endpoints[activeSection]} 
                    setActiveEndpoint={setActiveEndpoint}
                />;
            case 'webhooks':
                return <WebhooksSection copyCode={copyCode} copiedCode={copiedCode} />;
            case 'python':
            case 'javascript':
            case 'php':
            case 'curl':
                return <CodeExampleSection 
                    language={activeSection} 
                    code={codeExamples[activeSection]} 
                    copyCode={copyCode}
                    copiedCode={copiedCode}
                />;
            case 'integration':
                return <IntegrationSection />;
            default:
                return <OverviewSection />;
        }
    };

    return (
        <div className="flex h-screen bg-gray-50">
            {/* Sidebar Navigation */}
            <div className="w-80 bg-white shadow-lg overflow-y-auto">
                <div className="p-6 border-b">
                    <h2 className="text-2xl font-bold text-gray-900">API Documentation</h2>
                    <p className="text-sm text-gray-600 mt-1">Internal System API v1.0</p>
                </div>

                {/* Search */}
                <div className="p-4 border-b">
                    <input
                        type="text"
                        placeholder="Search endpoints..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                {/* Navigation */}
                <nav className="p-4">
                    {sections.map(section => (
                        <div key={section.id} className="mb-2">
                            <button
                                onClick={() => {
                                    if (section.subsections) {
                                        toggleSection(section.id);
                                    } else {
                                        setActiveSection(section.id);
                                    }
                                }}
                                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                                    activeSection === section.id || (section.subsections && section.subsections.some(sub => sub.id === activeSection))
                                        ? 'bg-blue-50 text-blue-700'
                                        : 'hover:bg-gray-100'
                                }`}
                            >
                                <span className="flex items-center">
                                    <span className="mr-2">{section.icon}</span>
                                    {section.title}
                                </span>
                                {section.subsections && (
                                    <span className="text-gray-400">
                                        {expandedSections.has(section.id) ? '−' : '+'}
                                    </span>
                                )}
                            </button>

                            {section.subsections && expandedSections.has(section.id) && (
                                <div className="ml-8 mt-1 space-y-1">
                                    {section.subsections.map(sub => (
                                        <button
                                            key={sub.id}
                                            onClick={() => setActiveSection(sub.id)}
                                            className={`w-full px-3 py-1.5 text-sm rounded text-left transition-colors ${
                                                activeSection === sub.id
                                                    ? 'bg-blue-50 text-blue-700'
                                                    : 'hover:bg-gray-100'
                                            }`}
                                        >
                                            {sub.title}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </nav>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-y-auto">
                <div className="max-w-5xl mx-auto p-8">
                    {renderContent()}
                </div>
            </div>

            {/* Endpoint Detail Modal */}
            {activeEndpoint && (
                <EndpointModal 
                    endpoint={activeEndpoint} 
                    onClose={() => setActiveEndpoint(null)}
                    copyCode={copyCode}
                    copiedCode={copiedCode}
                />
            )}
        </div>
    );
}

// Component sections
function OverviewSection() {
    return (
        <div className="prose max-w-none">
            <h1 className="text-3xl font-bold mb-4">Dentulu Internal System API</h1>
            <p className="text-lg text-gray-600 mb-6">
                The Dentulu Internal System API provides programmatic access to the platform's database schema, 
                relationships, features, and event registry. This API is designed for secure integration with 
                the Dentulu Ecosystem.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8 mb-8">
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-2">🔐 Secure Access</h3>
                    <p className="text-gray-600">
                        All endpoints require authentication using API keys with admin-level permissions.
                    </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-2">📊 Complete Schema Access</h3>
                    <p className="text-gray-600">
                        Access detailed information about all 156 tables, columns, relationships, and indexes.
                    </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-2">🔄 Real-time Events</h3>
                    <p className="text-gray-600">
                        Subscribe to webhooks for real-time notifications about data changes.
                    </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-2">🚀 Easy Integration</h3>
                    <p className="text-gray-600">
                        RESTful API with JSON responses and comprehensive error handling.
                    </p>
                </div>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Key Features</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Database Schema Discovery</strong> - Explore tables, columns, and data types</li>
                <li><strong>Relationship Mapping</strong> - Understand foreign key relationships</li>
                <li><strong>Feature Dependencies</strong> - Track which features use which tables</li>
                <li><strong>Event Registry</strong> - Subscribe to platform events via webhooks</li>
                <li><strong>Impact Analysis</strong> - Understand the impact of schema changes</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">API Base URL</h2>
            <div className="bg-gray-100 p-4 rounded-lg">
                <code className="text-lg">https://internal.sys.dentulu.com</code>
            </div>
        </div>
    );
}

function AuthenticationSection({ copyCode, copiedCode }) {
    const authExample = `curl -X GET https://internal.sys.dentulu.com/api/internal/schema \\
  -H "Authorization: Bearer your-api-key-here" \\
  -H "Content-Type: application/json"`;

    return (
        <div className="prose max-w-none">
            <h1 className="text-3xl font-bold mb-4">Authentication</h1>
            <p className="text-gray-600 mb-6">
                All API endpoints require authentication using an API key. API keys must be included in the 
                <code className="bg-gray-100 px-2 py-1 rounded">Authorization</code> header as a Bearer token.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Getting an API Key</h2>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
                <li>Log in to the Dentulu Admin Portal</li>
                <li>Navigate to the API Keys section</li>
                <li>Click "Generate New Key"</li>
                <li>Select "Admin" permissions for full access</li>
                <li>Copy and securely store your API key</li>
            </ol>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 my-6">
                <div className="flex">
                    <span className="text-yellow-600 mr-2">⚠️</span>
                    <div>
                        <p className="font-semibold text-yellow-800">Important</p>
                        <p className="text-sm text-yellow-700 mt-1">
                            API keys are shown only once. Store them securely and never expose them in client-side code.
                        </p>
                    </div>
                </div>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Using Your API Key</h2>
            <p className="text-gray-600 mb-4">Include your API key in the Authorization header of every request:</p>

            <div className="relative">
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                    <code>{authExample}</code>
                </pre>
                <button
                    onClick={() => copyCode(authExample, 'auth-example')}
                    className="absolute top-2 right-2 px-3 py-1 bg-gray-700 text-white text-sm rounded hover:bg-gray-600"
                >
                    {copiedCode === 'auth-example' ? 'Copied!' : 'Copy'}
                </button>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Security Best Practices</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Never share your API keys publicly</li>
                <li>Use environment variables to store API keys</li>
                <li>Rotate keys regularly (recommended: every 90 days)</li>
                <li>Use separate keys for different environments</li>
                <li>Monitor API key usage in the admin portal</li>
            </ul>
        </div>
    );
}

function ApiKeysSection() {
    return (
        <div className="prose max-w-none">
            <h1 className="text-3xl font-bold mb-4">API Key Management</h1>
            <p className="text-gray-600 mb-6">
                API keys are used to authenticate requests to the Internal System API. Keys can be managed
                through the admin portal or programmatically via the API.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Key Permissions</h2>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Permission Level</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Access</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        <tr>
                            <td className="px-6 py-4">
                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm">Read</span>
                            </td>
                            <td className="px-6 py-4 text-sm">Read-only access to all endpoints</td>
                            <td className="px-6 py-4 text-sm">GET requests only</td>
                        </tr>
                        <tr>
                            <td className="px-6 py-4">
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-sm">Write</span>
                            </td>
                            <td className="px-6 py-4 text-sm">Read and write access</td>
                            <td className="px-6 py-4 text-sm">All HTTP methods</td>
                        </tr>
                        <tr>
                            <td className="px-6 py-4">
                                <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-sm">Admin</span>
                            </td>
                            <td className="px-6 py-4 text-sm">Full access including key management</td>
                            <td className="px-6 py-4 text-sm">All endpoints and operations</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Key Rotation</h2>
            <p className="text-gray-600 mb-4">
                API keys can be rotated to maintain security. When a key is rotated:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>The old key is immediately invalidated</li>
                <li>A new key is generated with the same permissions</li>
                <li>You have 24 hours to update your applications</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Key Limits</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Maximum 10 active keys per user</li>
                <li>Keys can optionally expire after a set period</li>
                <li>Revoked keys cannot be reactivated</li>
            </ul>
        </div>
    );
}

function RateLimitingSection() {
    return (
        <div className="prose max-w-none">
            <h1 className="text-3xl font-bold mb-4">Rate Limiting</h1>
            <p className="text-gray-600 mb-6">
                To ensure fair usage and system stability, the API implements rate limiting on all endpoints.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Rate Limits</h2>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Endpoint Category</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Limit</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Window</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        <tr>
                            <td className="px-6 py-4 text-sm">Schema endpoints</td>
                            <td className="px-6 py-4 text-sm">1000 requests</td>
                            <td className="px-6 py-4 text-sm">Per hour</td>
                        </tr>
                        <tr>
                            <td className="px-6 py-4 text-sm">Relationship endpoints</td>
                            <td className="px-6 py-4 text-sm">1000 requests</td>
                            <td className="px-6 py-4 text-sm">Per hour</td>
                        </tr>
                        <tr>
                            <td className="px-6 py-4 text-sm">Feature endpoints</td>
                            <td className="px-6 py-4 text-sm">500 requests</td>
                            <td className="px-6 py-4 text-sm">Per hour</td>
                        </tr>
                        <tr>
                            <td className="px-6 py-4 text-sm">Event subscriptions</td>
                            <td className="px-6 py-4 text-sm">100 requests</td>
                            <td className="px-6 py-4 text-sm">Per hour</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Rate Limit Headers</h2>
            <p className="text-gray-600 mb-4">Every API response includes headers with rate limit information:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><code className="bg-gray-100 px-2 py-1 rounded">X-RateLimit-Limit</code> - Maximum requests allowed</li>
                <li><code className="bg-gray-100 px-2 py-1 rounded">X-RateLimit-Remaining</code> - Requests remaining in window</li>
                <li><code className="bg-gray-100 px-2 py-1 rounded">X-RateLimit-Reset</code> - Unix timestamp when limit resets</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Handling Rate Limits</h2>
            <p className="text-gray-600 mb-4">
                When rate limited, the API returns a <code className="bg-gray-100 px-2 py-1 rounded">429 Too Many Requests</code> status with a 
                <code className="bg-gray-100 px-2 py-1 rounded mx-1">Retry-After</code> header indicating when you can retry.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                    <strong>Tip:</strong> Implement exponential backoff and respect the Retry-After header 
                    to avoid being temporarily blocked.
                </p>
            </div>
        </div>
    );
}

function EndpointsSection({ category, endpoints, setActiveEndpoint }) {
    const categoryTitles = {
        schema: 'Schema Endpoints',
        relationships: 'Relationships Endpoints',
        features: 'Features Endpoints',
        events: 'Events Endpoints'
    };

    return (
        <div>
            <h1 className="text-3xl font-bold mb-6">{categoryTitles[category]}</h1>
            
            <div className="space-y-4">
                {endpoints.map((endpoint, index) => (
                    <div 
                        key={index}
                        className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer"
                        onClick={() => setActiveEndpoint(endpoint)}
                    >
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-3">
                                    <span className={`px-3 py-1 text-sm font-semibold rounded ${
                                        endpoint.method === 'GET' ? 'bg-green-100 text-green-800' :
                                        endpoint.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                                        endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                                        endpoint.method === 'DELETE' ? 'bg-red-100 text-red-800' :
                                        'bg-gray-100 text-gray-800'
                                    }`}>
                                        {endpoint.method}
                                    </span>
                                    <code className="text-sm font-mono">{endpoint.path}</code>
                                </div>
                                <span className="text-sm text-gray-500">
                                    Auth: {endpoint.auth || 'Required'}
                                </span>
                            </div>
                            <p className="text-gray-600">{endpoint.description}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function WebhooksSection({ copyCode, copiedCode }) {
    const webhookExample = {
        event: "appointment.created",
        timestamp: "2024-01-15T10:30:00Z",
        data: {
            id: "apt_123456",
            patient_id: "pat_789",
            provider_id: "doc_456",
            scheduled_at: "2024-01-20T14:00:00Z",
            duration_minutes: 30,
            type: "consultation"
        },
        metadata: {
            source: "dentulu_api",
            version: "1.0",
            delivery_attempt: 1
        }
    };

    const verificationExample = `const crypto = require('crypto');

function verifyWebhook(payload, signature, secret) {
    const hash = crypto
        .createHmac('sha256', secret)
        .update(payload)
        .digest('hex');
    
    return hash === signature;
}

// In your webhook handler
app.post('/webhook', (req, res) => {
    const signature = req.headers['x-dentulu-signature'];
    const payload = JSON.stringify(req.body);
    
    if (!verifyWebhook(payload, signature, process.env.WEBHOOK_SECRET)) {
        return res.status(401).send('Invalid signature');
    }
    
    // Process the webhook
    console.log('Webhook verified:', req.body);
    res.status(200).send('OK');
});`;

    return (
        <div className="prose max-w-none">
            <h1 className="text-3xl font-bold mb-4">Webhooks</h1>
            <p className="text-gray-600 mb-6">
                Webhooks allow you to receive real-time notifications when events occur in the Dentulu platform.
                Instead of polling for changes, webhooks push data to your endpoint as it happens.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Setting Up Webhooks</h2>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
                <li>Create an endpoint in your application to receive webhooks</li>
                <li>Subscribe to events using the <code className="bg-gray-100 px-2 py-1 rounded">/api/internal/events/subscribe</code> endpoint</li>
                <li>Verify webhook signatures for security</li>
                <li>Process events and respond with 200 OK</li>
            </ol>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Webhook Payload Format</h2>
            <p className="text-gray-600 mb-4">All webhooks follow a consistent JSON format:</p>
            
            <div className="relative">
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                    <code>{JSON.stringify(webhookExample, null, 2)}</code>
                </pre>
                <button
                    onClick={() => copyCode(JSON.stringify(webhookExample, null, 2), 'webhook-payload')}
                    className="absolute top-2 right-2 px-3 py-1 bg-gray-700 text-white text-sm rounded hover:bg-gray-600"
                >
                    {copiedCode === 'webhook-payload' ? 'Copied!' : 'Copy'}
                </button>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Webhook Security</h2>
            <p className="text-gray-600 mb-4">
                All webhooks are signed using HMAC-SHA256. Verify the signature to ensure the webhook 
                is from Dentulu:
            </p>

            <div className="relative">
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                    <code>{verificationExample}</code>
                </pre>
                <button
                    onClick={() => copyCode(verificationExample, 'webhook-verify')}
                    className="absolute top-2 right-2 px-3 py-1 bg-gray-700 text-white text-sm rounded hover:bg-gray-600"
                >
                    {copiedCode === 'webhook-verify' ? 'Copied!' : 'Copy'}
                </button>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Retry Policy</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Webhooks are retried up to 3 times with exponential backoff</li>
                <li>Retry intervals: 1 minute, 5 minutes, 30 minutes</li>
                <li>Failed webhooks after all retries are logged and can be manually resent</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Best Practices</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Always verify webhook signatures</li>
                <li>Respond quickly (within 5 seconds) with 200 OK</li>
                <li>Process webhooks asynchronously if needed</li>
                <li>Implement idempotency to handle duplicate deliveries</li>
                <li>Log all received webhooks for debugging</li>
            </ul>
        </div>
    );
}

function CodeExampleSection({ language, code, copyCode, copiedCode }) {
    const languageNames = {
        python: 'Python',
        javascript: 'JavaScript',
        php: 'PHP',
        curl: 'cURL'
    };

    const languageStyles = {
        python: 'language-python',
        javascript: 'language-javascript',
        php: 'language-php',
        curl: 'language-bash'
    };

    return (
        <div>
            <h1 className="text-3xl font-bold mb-6">{languageNames[language]} Example</h1>
            
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">Complete Integration Example</h2>
                <p className="text-gray-600 mb-4">
                    This example shows how to connect to the API and retrieve data using {languageNames[language]}.
                </p>
                
                <div className="relative">
                    <pre className={`bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto ${languageStyles[language]}`}>
                        <code>{code}</code>
                    </pre>
                    <button
                        onClick={() => copyCode(code, `${language}-example`)}
                        className="absolute top-2 right-2 px-3 py-1 bg-gray-700 text-white text-sm rounded hover:bg-gray-600"
                    >
                        {copiedCode === `${language}-example` ? 'Copied!' : 'Copy'}
                    </button>
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-2">Next Steps</h3>
                    <ul className="text-sm text-blue-800 space-y-1">
                        <li>• Replace <code className="bg-blue-100 px-1 rounded">your-api-key-here</code> with your actual API key</li>
                        <li>• Adjust the base URL if using a different environment</li>
                        <li>• Add error handling for production use</li>
                        <li>• Implement caching for frequently accessed data</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}

function IntegrationSection() {
    return (
        <div className="prose max-w-none">
            <h1 className="text-3xl font-bold mb-4">Integration Guide</h1>
            <p className="text-gray-600 mb-6">
                Follow this guide to integrate the Dentulu Internal System API into your application.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Step 1: Obtain API Credentials</h2>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
                <li>Log in to the Dentulu Admin Portal</li>
                <li>Navigate to Settings → API Keys</li>
                <li>Generate a new API key with appropriate permissions</li>
                <li>Store the API key securely in your environment variables</li>
            </ol>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Step 2: Set Up Your Development Environment</h2>
            <p className="text-gray-600 mb-4">Install the required dependencies for your programming language:</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold mb-2">Python</h4>
                    <code className="text-sm">pip install requests</code>
                </div>
                <div className="bg-gray-50 p-4 rounded">
                    <h4 className="font-semibold mb-2">Node.js</h4>
                    <code className="text-sm">npm install axios</code>
                </div>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Step 3: Make Your First API Call</h2>
            <p className="text-gray-600 mb-4">Start with a simple GET request to verify your connection:</p>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
                <li>Use the <code className="bg-gray-100 px-2 py-1 rounded">/api/internal/schema</code> endpoint</li>
                <li>Include your API key in the Authorization header</li>
                <li>Verify you receive a 200 OK response</li>
            </ol>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Step 4: Implement Core Features</h2>
            <p className="text-gray-600 mb-4">Based on your use case, implement the following:</p>
            
            <div className="space-y-4">
                <div className="bg-white border rounded-lg p-4">
                    <h4 className="font-semibold mb-2">📊 Schema Discovery</h4>
                    <p className="text-sm text-gray-600">
                        Use schema endpoints to understand the database structure and plan your integration.
                    </p>
                </div>
                <div className="bg-white border rounded-lg p-4">
                    <h4 className="font-semibold mb-2">🔗 Relationship Mapping</h4>
                    <p className="text-sm text-gray-600">
                        Query relationships to understand how data is connected across tables.
                    </p>
                </div>
                <div className="bg-white border rounded-lg p-4">
                    <h4 className="font-semibold mb-2">🎯 Event Subscriptions</h4>
                    <p className="text-sm text-gray-600">
                        Set up webhooks to receive real-time updates about data changes.
                    </p>
                </div>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Step 5: Production Deployment</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Use environment variables for API keys</li>
                <li>Implement proper error handling and retries</li>
                <li>Set up monitoring and alerting</li>
                <li>Cache frequently accessed data</li>
                <li>Implement request throttling to stay within rate limits</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Common Integration Patterns</h2>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">1. Data Synchronization</h3>
            <p className="text-gray-600 mb-4">
                Use webhooks to keep your data in sync with Dentulu. Subscribe to relevant events and 
                update your local database when changes occur.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">2. Analytics and Reporting</h3>
            <p className="text-gray-600 mb-4">
                Query schema and relationships to build custom reports and analytics dashboards based 
                on Dentulu data.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">3. Feature Integration</h3>
            <p className="text-gray-600 mb-4">
                Use the features endpoint to understand dependencies and build complementary features 
                that work seamlessly with Dentulu.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Support and Resources</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>API Status: <a href="https://status.dentulu.com" className="text-blue-600 hover:underline">status.dentulu.com</a></li>
                <li>Developer Forum: <a href="https://developers.dentulu.com/forum" className="text-blue-600 hover:underline">developers.dentulu.com/forum</a></li>
                <li>Support Email: <a href="mailto:api-support@dentulu.com" className="text-blue-600 hover:underline">api-support@dentulu.com</a></li>
            </ul>
        </div>
    );
}

function EndpointModal({ endpoint, onClose, copyCode, copiedCode }) {
    const exampleResponse = endpoint.response ? JSON.stringify(endpoint.response, null, 2) : '{\n  "success": true,\n  "data": {...}\n}';
    
    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
                <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                            <span className={`px-3 py-1 text-sm font-semibold rounded ${
                                endpoint.method === 'GET' ? 'bg-green-100 text-green-800' :
                                endpoint.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                            }`}>
                                {endpoint.method}
                            </span>
                            <code className="text-lg font-mono">{endpoint.path}</code>
                        </div>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">
                            ×
                        </button>
                    </div>
                    
                    <div className="p-6">
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold mb-2">Description</h3>
                            <p className="text-gray-600">{endpoint.description}</p>
                        </div>

                        {endpoint.params && (
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold mb-2">Path Parameters</h3>
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-sm">Name</th>
                                            <th className="px-4 py-2 text-left text-sm">Type</th>
                                            <th className="px-4 py-2 text-left text-sm">Required</th>
                                            <th className="px-4 py-2 text-left text-sm">Description</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {endpoint.params.map((param, index) => (
                                            <tr key={index}>
                                                <td className="px-4 py-2 text-sm font-mono">{param.name}</td>
                                                <td className="px-4 py-2 text-sm">{param.type}</td>
                                                <td className="px-4 py-2 text-sm">
                                                    {param.required ? '✓' : '-'}
                                                </td>
                                                <td className="px-4 py-2 text-sm">{param.description}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {endpoint.body && (
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold mb-2">Request Body</h3>
                                <div className="relative">
                                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                                        <code>{JSON.stringify(endpoint.body, null, 2)}</code>
                                    </pre>
                                    <button
                                        onClick={() => copyCode(JSON.stringify(endpoint.body, null, 2), 'request-body')}
                                        className="absolute top-2 right-2 px-3 py-1 bg-gray-700 text-white text-sm rounded hover:bg-gray-600"
                                    >
                                        {copiedCode === 'request-body' ? 'Copied!' : 'Copy'}
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="mb-6">
                            <h3 className="text-lg font-semibold mb-2">Example Response</h3>
                            <div className="relative">
                                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                                    <code>{exampleResponse}</code>
                                </pre>
                                <button
                                    onClick={() => copyCode(exampleResponse, 'response-example')}
                                    className="absolute top-2 right-2 px-3 py-1 bg-gray-700 text-white text-sm rounded hover:bg-gray-600"
                                >
                                    {copiedCode === 'response-example' ? 'Copied!' : 'Copy'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}